import { css } from '@emotion/react';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';
import { useCallback } from 'react';
import {
  EmptyStateComponent, EmptyStateSize,
} from '~/_shared/components/emptyState/emptyState.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { type MetricValue } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { DataWeightsItemComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/dataWeightsItem.component';

const LINE_SIZE = 65;
const METRICS_PADDING = 16;

const container = css({
  minHeight: LINE_SIZE,
  maxHeight: 200,
});

const content = css({
  padding: METRICS_PADDING,
});

type DataWeightsProps = {
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  onChangeDataMetrics: (dataMetricsValues: readonly MetricValue[]) => void;
  onChangeDemographics: (demographicsValues: readonly MetricValue[]) => void;
  minWeight: number;
  maxWeight: number;
};

export const DataWeightsComponent = ({
  dataMetricsValues,
  onChangeDataMetrics,
  demographicsValues,
  onChangeDemographics,
  minWeight,
  maxWeight,
}: DataWeightsProps) => {
  const [t] = useTranslation();

  const onChangeDataMetricsSlider = useCallback((id: string, weight: number) => {
    const newMetricValues = dataMetricsValues.map(dataMetricsValue => ({
      ...dataMetricsValue,
      weight: dataMetricsValue.id === id ? weight : dataMetricsValue.weight,
    }));
    onChangeDataMetrics(newMetricValues);
  }, [onChangeDataMetrics, dataMetricsValues]);

  const onChangeDemographicsSlider = useCallback((id: string, weight: number) => {
    const newMetricValues = demographicsValues.map(demographicsValue => ({
      ...demographicsValue,
      weight: demographicsValue.id === id ? weight : demographicsValue.weight,
    }));
    onChangeDemographics(newMetricValues);
  }, [onChangeDemographics, demographicsValues]);

  const renderSliders = (metricsValues: readonly MetricValue[], onChange: (id: string, weight: number) => void) => {
    return metricsValues
      .filter(metricsValue => metricsValue.checked)
      .map(metricsValue => (
        <DataWeightsItemComponent
          key={metricsValue.id}
          metric={metricsValue}
          onChange={(newMetric) => onChange(newMetric.id, newMetric.weight)}
          minWeight={minWeight}
          maxWeight={maxWeight}
        />
      ));
  };

  const numberOfMetrics = dataMetricsValues.concat(demographicsValues).filter(v => v.checked).length;

  return (
    <div
      css={container}
      style={{ height: numberOfMetrics ? ((numberOfMetrics * LINE_SIZE) + METRICS_PADDING) : undefined }}
    >
      {numberOfMetrics ? (
        <ScrollBarComponent
          type={ScrollbarType.Vertical}
          maxHeight={200}
        >
          <div css={content}>
            {renderSliders(dataMetricsValues, onChangeDataMetricsSlider)}
            {renderSliders(demographicsValues, onChangeDemographicsSlider)}
          </div>
        </ScrollBarComponent>
      ) : (
        <EmptyStateComponent
          css={{ padding: 10 }}
          icon={faSliders}
          title={t('No Metrics Selected')}
          size={EmptyStateSize.Small}
        />
      )}
    </div>
  );
};
